import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  Placement as TypePlacement,
  ReferenceObject as TypeReferenceObject,
} from 'popper.js';
import { Popper, PopperProps } from 'react-popper';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';
import { useEventListener } from '../hooks';
import { transitionDurationFast } from '../variables';
import Base, { Props as BaseProps } from '../Base/Base';
import { PlacementArrowPropsContext } from './PlacementArrowPropsContext';
import { PlacementManagerContext } from './PlacementManagerContext';
import './Placement.css';

const { useEffect, useRef, useState } = React;

const transitionClasses = {
  appear: 'Placement__content-appear',
  appearActive: 'Placement__content-appear-active',
  appearDone: 'Placement__content-appear-done',
  enter: 'Placement__content-enter',
  enterActive: 'Placement__content-enter-active',
  enterDone: 'Placement__content-enter-done',
  exit: 'Placement__content-exit',
  exitActive: 'Placement__content-exit-active',
  exitDone: 'Placement__content-exit-done',
};

export interface Props extends BaseProps {
  animatePosition?: boolean;
  children: (props: {
    scheduleUpdate: () => void;
    width?: number;
  }) => React.ReactNode;
  closeElement?: null | HTMLElement;
  disableReposition?: boolean;
  flip?: boolean;
  isInsideModal?: boolean;
  onClose?: (event: MouseEvent) => void;
  placement?: TypePlacement;
  referenceElement?: TypeReferenceObject;
  visible?: boolean;
  timeout?: number;
}

const PlacementPopper: React.FunctionComponent<Props> = (props: Props) => {
  const {
    animatePosition,
    backgroundColor,
    children,
    closeElement,
    disableReposition,
    flip,
    isInsideModal,
    onClose,
    placement: desiredPlacement,
    referenceElement,
    visible,
    timeout = transitionDurationFast,
    ...rest
  } = props;

  const ref = useRef<HTMLElement | null>();
  const [render, setRender] = useState(visible);

  useEffect(() => {
    if (visible) {
      setRender(true);
    }
  }, [visible]);

  useEventListener(closeElement || document, 'click', (event) => {
    if (onClose && ref.current && !ref.current.contains(event.target as Node)) {
      onClose(event as MouseEvent);
    }
  }, [onClose]);

  useEventListener(ref.current || null, 'transitionend', () => {
    if (!visible) {
      setRender(false);
    }
  }, [visible]);

  if (!render) {
    return null;
  }

  const popperProps: PopperProps = {
    children: () => null,
    eventsEnabled: !disableReposition,
    placement: desiredPlacement,
    modifiers: {
      preventOverflow: {
        boundariesElement: 'window',
      },
      flip: {
        behavior: 'flip',
        enabled: flip,
      },
    },
  };

  if (referenceElement) {
    popperProps.referenceElement = referenceElement;
  }

  return createPortal(
    <Popper { ...popperProps } innerRef={ (el) => ref.current = el || null }>
      { ({ arrowProps, placement, ref, scheduleUpdate, style }) =>
        <PlacementArrowPropsContext.Provider value={ { ...arrowProps, backgroundColor } }>
          <div
              className={ classnames('Placement', `Placement--${placement}`, {
                'Placement--above-modal': isInsideModal,
                'Placement--animate': animatePosition,
              }) }
              ref={ ref }
              style={ style }>
            <CSSTransition
                appear={ visible }
                classNames={ transitionClasses }
                in={ visible }
                timeout={ timeout }>
              <div className="Placement__content">
                <Base { ...rest }
                    backgroundColor={ backgroundColor }
                    className="Placement__shadow">
                  <PlacementManagerContext.Consumer>
                    { ({ referenceNode }) =>
                      children({
                        scheduleUpdate: scheduleUpdate,
                        width: referenceNode && referenceNode.clientWidth || undefined,
                      })
                    }
                  </PlacementManagerContext.Consumer>
                </Base>
              </div>
            </CSSTransition>
          </div>
        </PlacementArrowPropsContext.Provider>
      }
    </Popper>,
    document.body
  );
};

PlacementPopper.defaultProps = {
  backgroundColor: 'white',
  borderRadius: 'x2',
};

export default PlacementPopper;
